import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { formatDmy, formatPaidDate } from "../../utils/dateFormat";

// Jendela edit pembayaran satu kelas di Financial Report.
// editMode "full" → status, tanggal, subsidi, metode, nominal + alasan wajib.
// editMode "note" → hanya catatan (kelas dibayar online/terkunci, CEA To
// Church, link pembayaran masih menunggu, kelas ulangan).

const rp = (n) => (typeof n === "number" ? `Rp${n.toLocaleString("id-ID")}` : "-");
const jakartaDate = (iso) =>
  iso ? new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date(iso)) : "";
const todayJakarta = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());

export const METHOD_LABEL = {
  gateway: "Online payment link (locked)",
  transfer: "Manual bank transfer",
  cash: "Cash",
  admin: "Marked paid by admin",
  legacy: "Old record (method unknown)",
};
const EDITABLE_METHODS = ["transfer", "cash", "admin", "legacy"];

const FIELD_LABEL = {
  status: "Status",
  paidAt: "Payment date",
  discountPercent: "CEA subsidy",
  paymentMethod: "Method",
  orderId: "Transaction ID",
  amountPaid: "Amount paid",
};
const ACTION_LABEL = { edit: "Edited", note: "Note", edit_student: "Changed in Edit Student" };

const formatValue = (field, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "status") return v === "completed" ? "Paid" : "Unpaid";
  if (field === "paidAt") return formatDmy(v) || String(v);
  if (field === "discountPercent") return `${v}%`;
  if (field === "paymentMethod") return METHOD_LABEL[v] || v;
  if (field === "amountPaid") return rp(Number(v));
  return String(v);
};

const swalError = (text) =>
  Swal.fire({ text, icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });

const ClassPaymentModal = ({ row, cls, onClose, onSaved }) => {
  const editable = cls.editMode === "full";
  const normal = cls.amounts ? cls.amounts.normal : null;

  const [status, setStatus] = useState(cls.status === "paid" ? "paid" : "unpaid");
  const [paidDate, setPaidDate] = useState(jakartaDate(cls.paidAt) || todayJakarta());
  const [discount, setDiscount] = useState(String(cls.split ? cls.split.cea : row.split.cea));
  const [method, setMethod] = useState(EDITABLE_METHODS.includes(cls.paymentMethod) ? cls.paymentMethod : "transfer");
  const [amountPaid, setAmountPaid] = useState(cls.amountPaid != null ? String(cls.amountPaid) : "");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);

  const target = { studentRowId: row.id, classCode: cls.code, batch: cls.batch };

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get("/financial-report/class-history", { params: target });
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, cls.code, cls.batch]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  // Pratinjau nominal sesuai isian.
  const pct = Math.min(Math.max(parseInt(discount, 10) || 0, 0), 100);
  const customAmount = amountPaid.trim() === "" ? null : Number(amountPaid);
  const previewStudent =
    normal == null ? null : customAmount != null && Number.isFinite(customAmount) ? customAmount : Math.round((normal * (100 - pct)) / 100);
  const previewCea = normal == null || previewStudent == null ? null : Math.max(normal - previewStudent, 0);

  const save = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) {
      swalError("Please write the reason for this change.");
      return;
    }
    if (status === "unpaid" && cls.status === "paid") {
      const ok = await Swal.fire({
        html:
          `Mark <strong>${cls.code}</strong> as <strong>Unpaid</strong>? Its payment date, subsidy and method will be cleared.` +
          (row.paymentPlan === "installment"
            ? "<br/><br/>This class will become billable again on the Installments page."
            : "<br/><br/>This student is not on an installment plan, so the class will not be billed automatically."),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Yes, mark unpaid",
        background: "white",
        color: "black",
      });
      if (!ok.isConfirmed) return;
    }
    setSaving(true);
    try {
      const res = await axios.put("/financial-report/class", {
        ...target,
        status,
        paidDate,
        discountPercent: pct,
        paymentMethod: method,
        amountPaid: amountPaid.trim() === "" ? null : Number(amountPaid),
        reason,
      });
      await onSaved();
      onClose();
      Swal.fire({
        text: res.data.warning || "Saved.",
        icon: res.data.warning ? "info" : "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        timer: res.data.warning ? undefined : 1600,
        showConfirmButton: !!res.data.warning,
      });
    } catch (err) {
      swalError(err.response?.data?.msg || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (note.trim().length < 3) {
      swalError("Please write the note.");
      return;
    }
    setSaving(true);
    try {
      await axios.post("/financial-report/class-note", { ...target, note });
      setNote("");
      await Promise.all([loadHistory(), onSaved()]);
    } catch (err) {
      swalError(err.response?.data?.msg || "Failed to add the note.");
    } finally {
      setSaving(false);
    }
  };

  const a = cls.amounts;

  return createPortal(
    <div className="adm-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className="adm-modal fr-edit" style={{ width: "720px" }} onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal-header">
          <div>
            <h5 className="adm-card-title" style={{ fontSize: "17px" }}>
              {row.name} — {cls.code}
            </h5>
            <p className="adm-card-subtitle">
              {cls.name} · {cls.month}
              {cls.batch ? ` · Batch ${cls.batch}` : ""} · {row.studentId}
            </p>
          </div>
          <button type="button" className="adm-modal-close" aria-label="Close" onClick={onClose} disabled={saving}>
            ×
          </button>
        </div>

        <div className="adm-modal-body">
          {/* Ringkasan saat ini */}
          <div className="fr-edit-summary">
            <div>
              <span>Normal price</span>
              <strong>{rp(a?.normal)}</strong>
            </div>
            <div>
              <span>Student</span>
              <strong>{rp(a?.student)}</strong>
            </div>
            {a && a.pic > 0 && (
              <div>
                <span>PIC</span>
                <strong>{rp(a.pic)}</strong>
              </div>
            )}
            <div>
              <span>CEA subsidy</span>
              <strong>{rp(a?.cea)}</strong>
            </div>
            <div>
              <span>Status</span>
              <strong>
                {cls.status === "paid" ? `Paid${cls.paidAt || cls.paidDateUnknown ? ` · ${formatPaidDate(cls.paidAt, cls.paidDateUnknown)}` : ""}` : cls.status === "unpaid" ? "Unpaid" : cls.status}
              </strong>
            </div>
            <div>
              <span>Method</span>
              <strong>{cls.paymentMethod ? METHOD_LABEL[cls.paymentMethod] || cls.paymentMethod : "Not recorded"}</strong>
              {cls.orderId && <small>{cls.orderId}</small>}
            </div>
          </div>

          {!editable && (
            <div className="fr-edit-locked">
              <i className={`bi ${cls.paymentMethod === "gateway" ? "bi-lock-fill" : "bi-info-circle"}`}></i>
              <span>{cls.editBlockedReason || "Only notes can be added to this class."}</span>
            </div>
          )}

          {editable && (
            <form onSubmit={save} className="fr-edit-form">
              <p className="fr-edit-title">Edit payment</p>
              <div className="fr-edit-status">
                {["paid", "unpaid"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`fr-edit-toggle${status === s ? " is-on" : ""}`}
                    onClick={() => setStatus(s)}
                  >
                    {s === "paid" ? "Paid" : "Unpaid"}
                  </button>
                ))}
              </div>

              {status === "paid" && (
                <div className="row g-3" style={{ marginTop: "2px" }}>
                  <div className="col-sm-6">
                    <label className="form-label" htmlFor="fr-date">Payment date</label>
                    <input
                      id="fr-date"
                      type="date"
                      className="form-control"
                      value={paidDate}
                      max={todayJakarta()}
                      onChange={(e) => setPaidDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label" htmlFor="fr-method">Method</label>
                    <select id="fr-method" className="form-select" value={method} onChange={(e) => setMethod(e.target.value)}>
                      {EDITABLE_METHODS.map((m) => (
                        <option key={m} value={m}>{METHOD_LABEL[m]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label" htmlFor="fr-discount">CEA subsidy (%)</label>
                    <input
                      id="fr-discount"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      className="form-control"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      disabled={customAmount != null}
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label" htmlFor="fr-amount">
                      Amount paid <span className="fr-optional">(optional)</span>
                    </label>
                    <input
                      id="fr-amount"
                      type="number"
                      min="0"
                      max={normal ?? undefined}
                      step="1"
                      className="form-control"
                      placeholder={normal == null ? "No price for this program" : `Leave empty to use the subsidy`}
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(e.target.value)}
                      disabled={normal == null}
                    />
                  </div>
                  {normal != null && (
                    <div className="col-12">
                      <p className="fr-edit-preview">
                        Student pays <strong>{rp(previewStudent)}</strong> · CEA subsidy <strong>{rp(previewCea)}</strong>
                        {customAmount != null && " (from the amount paid)"}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {status === "unpaid" && (
                <p className="adm-help" style={{ margin: "10px 0 0" }}>
                  {row.paymentPlan === "installment"
                    ? "The class will become billable again on the Installments page."
                    : "This student is not on an installment plan, so the class will not be billed automatically."}
                </p>
              )}

              <label className="form-label" htmlFor="fr-reason" style={{ marginTop: "14px" }}>
                Reason for this change <span className="fr-required">*</span>
              </label>
              <textarea
                id="fr-reason"
                className="form-control"
                rows={2}
                placeholder="e.g. Paid by BCA transfer on 20 Oct — proof in WhatsApp"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="d-flex justify-content-end" style={{ marginTop: "12px" }}>
                <button type="submit" className="adm-btn adm-btn--primary" disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          )}

          {/* Catatan — selalu bisa, termasuk kelas terkunci (mis. refund) */}
          <div className="fr-edit-form">
            <p className="fr-edit-title">Add a note</p>
            <textarea
              className="form-control"
              rows={2}
              placeholder="e.g. Refund Rp879.900 on 12 Oct — paid twice by mistake"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              aria-label="Note"
            />
            <div className="d-flex justify-content-end" style={{ marginTop: "10px" }}>
              <button type="button" className="adm-btn adm-btn--secondary" onClick={addNote} disabled={saving}>
                Add note
              </button>
            </div>
          </div>

          {/* Riwayat */}
          <p className="fr-edit-title" style={{ marginTop: "6px" }}>History</p>
          {history === null ? (
            <p className="adm-help">Loading…</p>
          ) : history.length === 0 ? (
            <p className="adm-help">No changes or notes yet.</p>
          ) : (
            <ul className="fr-history">
              {history.map((h) => (
                <li key={h.id}>
                  <div className="fr-history-head">
                    <span className={`fr-history-action is-${h.action}`}>{ACTION_LABEL[h.action] || h.action}</span>
                    <span>
                      {new Date(h.createdAt).toLocaleString("en-GB", { timeZone: "Asia/Jakarta", dateStyle: "medium", timeStyle: "short" })}
                      {h.adminName ? ` · ${h.adminName}` : ""}
                    </span>
                  </div>
                  {h.changes && (
                    <div className="fr-history-changes">
                      {Object.entries(h.changes).map(([f, [before, after]]) => (
                        <div key={f}>
                          {FIELD_LABEL[f] || f}: {formatValue(f, before)} → <strong>{formatValue(f, after)}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  {h.note && <p className="fr-history-note">{h.note}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ClassPaymentModal;
